/* What usage and cost mean on screen, decided once.
 *
 * The judgement here is that an *unpriced* meter is the thing worth showing, not a footnote. A
 * meter with usage and no rate is a configuration gap — the platform reports it rather than
 * charging zero, and a screen that omitted it would recreate the failure the API avoids: usage
 * that reads as free because nobody decided what it costs.
 */

import type {
  CostSummary,
  Meter,
  Quota,
  UsageLine,
} from '@/shared/api/generated/metering';

export type QuotaTone = 'ok' | 'warn' | 'breach';

/** Where a meter's flushed usage stands against its limit. */
export function quotaTone(quota: Quota, consumed: number): QuotaTone {
  if (consumed >= quota.unit_limit) return 'breach';
  if (consumed >= quota.soft_limit) return 'warn';
  return 'ok';
}

export function meterLabelKey(meter: Meter): string {
  return `metering.meter.${meter}`;
}

/** Largest first — the meter driving the bill is the one somebody is looking for. */
export function orderUsage(lines: readonly UsageLine[]): UsageLine[] {
  return [...lines].sort((left, right) => right.units - left.units);
}

/** Indexed by meter so a quota row can show what has been consumed against it. */
export function usageByMeter(lines: readonly UsageLine[]): Map<Meter, number> {
  return new Map(lines.map((line) => [line.meter, line.units]));
}

/**
 * True when the tenant used something the rate card does not price.
 *
 * Separate from `summary.unpriced_meters.length > 0` at the call site so the screen states the
 * condition once: this is a *warning* about billing configuration, not an error, and conflating it
 * with a failed request is how an operator learns to ignore both.
 */
export function hasUnpricedUsage(summary: CostSummary): boolean {
  return summary.unpriced_meters.length > 0;
}
